import { defineStore } from "pinia";
import dayjs from "dayjs";
import { child, get, onValue } from "firebase/database";
import { getDoc, onSnapshot, runTransaction, Timestamp } from "firebase/firestore";
import { db } from "~/firebaseInstance";

// Receipt running number (the XXXX in YYYYMM-XXXX). Resets every month.
//
// - localStorage issues the numbers. Every claim re-reads it fresh, so all
//   tabs of this machine share one sequence — online or offline. Keys are
//   storeId-scoped (one machine can serve several shops).
// - Firestore is the cloud ledger other terminals sync from. Each sale queues
//   one bump; the transaction is idempotent (nonce doc), so replaying a bump
//   can never double-count.
// - Cloud values only ever RAISE the local counter (max), so an issued number
//   is never issued twice.
// - TRANSITION: the legacy RTDB counter is folded in read-only. Delete the
//   two marked spots + the firebase/database imports after fleet rollout.

type CounterIncrement = {
  nonce: string; // the receipt's Nonce; also the Firestore idempotency doc id
  month: string; // YYYYMM bucket
  claimed?: number; // number this sale issued (absent on legacy items)
  createdAt: number;
  failCount?: number; // consecutive flush failures; parked past the cap
};

export const usePosReceiptIdCounterStore = defineStore("receiptIdCounter", () => {
  const receiptsCounter = ref(0);
  const counterYYYYMM = ref(dayjs().format("YYYYMM"));
  const loaded = ref(false);

  // Shop the listeners were bound to at boot — a stale callback after an
  // in-tab shop switch must not write into the new shop's keys.
  let bootedStoreId: string | null = null;

  const storeId = () => useAuthStore().storeId ?? "";
  const isOnline = () =>
    typeof navigator === "undefined" || navigator.onLine !== false;

  const counterKey = () => `receiptsCounter:${storeId()}:${counterYYYYMM.value}`;
  const queuePrefix = () => `counter-increment:${storeId()}:`;
  // Poison items (rules denial, malformed month/nonce) used to be retried on
  // EVERY sale + 10-min tick + reconnect, forever. Past the cap the item is
  // moved here — kept, not deleted (receipt numbering matters) — where
  // readQueue's prefix checks no longer match it.
  const parkedPrefix = () => `counter-increment-parked:${storeId()}:`;
  const MAX_FLUSH_FAILURES = 20;

  const readLocal = (): number => {
    try {
      const n = parseInt(localStorage.getItem(counterKey()) ?? "", 10);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  // Never lowers: takes the max of memory, localStorage and the new value.
  const raiseTo = (value: number) => {
    receiptsCounter.value = Math.max(receiptsCounter.value, readLocal(), value);
    try {
      localStorage.setItem(counterKey(), String(receiptsCounter.value));
    } catch {}
  };

  // ── Claim ────────────────────────────────────────────────────────
  // Re-reads localStorage fresh, so a sibling tab's sale is always seen.
  // `nonce` is the receipt's own Nonce (passed in by the payment flow) —
  // one id ties the bill, its queued bump and the Firestore idempotency doc.
  const getNextReceiptIdAndIncrement = (nonce: string): number => {
     
    const next = Math.max(receiptsCounter.value, readLocal()) + 1;
    receiptsCounter.value = next;
    try {
      localStorage.setItem(counterKey(), String(next));
      const cmd: CounterIncrement = {
        nonce,
        month: counterYYYYMM.value,
        claimed: next,
        createdAt: Date.now(),
      };
      localStorage.setItem(`${queuePrefix()}${nonce}`, JSON.stringify(cmd));
    } catch (error) {
      console.error("Failed to persist receipt counter claim:", error);
    }
    void flushCounterIncrements();
    return next;
  };

  // ── Cloud sync queue ─────────────────────────────────────────────
  // Legacy keys from the old app version (`counter-increment:{nonce}`, one
  // `:`) are adopted by the current shop; scoped keys of OTHER shops wait
  // for their own login.
  const readQueue = (): { cmd: CounterIncrement; key: string }[] => {
    const out: { cmd: CounterIncrement; key: string }[] = [];
    try {
      for (const key of Object.keys(localStorage)) {
        const isMine = key.startsWith(queuePrefix());
        const isLegacy = key.startsWith("counter-increment:") && key.split(":").length === 2;
        if (!isMine && !isLegacy) continue;
        const raw = localStorage.getItem(key);
        if (raw) out.push({ cmd: JSON.parse(raw), key });
      }
    } catch (error) {
      console.error("Failed to read counter queue:", error);
    }
    return out.sort((a, b) => a.cmd.createdAt - b.cmd.createdAt); // sale order
  };

  // Firestore auto-cleanup: `expireAt` + a TTL policy in the console (field
  // `expireAt`, collection group `nonces`) deletes each idempotency marker
  // ~90 days after its sale — no client-side cleanup code needed.
  const FIRESTORE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

  // Idempotent bump: the nonce doc and the counter raise commit in ONE
  // transaction — a replay sees the nonce and does nothing. Raising to at
  // least `claimed` keeps one sequence even when queues land out of order.
  const applyToCloud = async (cmd: CounterIncrement) => {
    const fs = useFirestore();
    const counterRef = fs.getReceiptCounterDocRef(cmd.month);
    const nonceRef = fs.getReceiptCounterNonceDocRef(cmd.month, cmd.nonce);
    const expireAt = Timestamp.fromMillis(Date.now() + FIRESTORE_TTL_MS);
    await runTransaction(db, async (tx) => {
      if ((await tx.get(nonceRef)).exists()) return;
      const base = Number((await tx.get(counterRef)).data()?.value) || 0;
      tx.set(counterRef, { value: Math.max(base + 1, Number(cmd.claimed) || 0) }, { merge: true });
      tx.set(nonceRef, { createdAt: cmd.createdAt, expireAt });
    });
  };

  // Failed items stay queued for the next flush (next sale / reconnect /
  // 10-min tick). Overlapping flushes are harmless — the transaction is
  // idempotent — the flag just avoids redundant round-trips.
  let flushing = false;
  const flushCounterIncrements = async () => {
    if (flushing || !isOnline() || !storeId()) return;
    flushing = true;
    try {
      for (const { cmd, key } of readQueue()) {
        try {
          await applyToCloud(cmd);
          localStorage.removeItem(key);
        } catch (error) {
          console.error("Failed to flush counter increment:", cmd.nonce, error);
          try {
            const failCount = (cmd.failCount || 0) + 1;
            if (failCount >= MAX_FLUSH_FAILURES) {
              localStorage.setItem(`${parkedPrefix()}${cmd.nonce}`, JSON.stringify({ ...cmd, failCount }));
              localStorage.removeItem(key);
              console.error(`Counter increment ${cmd.nonce} parked after ${failCount} failed flushes`);
            } else {
              localStorage.setItem(key, JSON.stringify({ ...cmd, failCount }));
            }
          } catch {}
        }
      }
    } finally {
      flushing = false;
    }
  };

  // ── Cloud → local ────────────────────────────────────────────────
  const onCloudValue = (cloudVal: number) => {
    raiseTo(cloudVal);
  };

  const watchCloudCounter = () => {
    const month = counterYYYYMM.value;

    onSnapshot(
      useFirestore().getReceiptCounterDocRef(month),
      (snap) => onCloudValue(Number(snap.data()?.value) || 0),
    );

    // TRANSITION: legacy RTDB counter, read-only (old app versions still bump it).
    onValue(child(useRealtimeDatabase().lastReceiptIdRef(), month), (snap) => {
      onCloudValue(Number(snap.val()) || 0);
    });
  };

  // `storage` fires in OTHER tabs (works offline) — a sibling tab's claim
  // updates this tab immediately.
  const watchCrossTabCounter = () => {
    window.addEventListener("storage", (e) => {
      if (e.key !== counterKey()) return;
      const n = parseInt(e.newValue ?? "", 10);
      if (Number.isFinite(n) && n > receiptsCounter.value) receiptsCounter.value = n;
    });
  };

  // ── Boot ─────────────────────────────────────────────────────────
  // First run for this shop/month on this machine (no local value yet):
  // read both clouds once BEFORE going ready, so the sequence continues from
  // the existing number (RTDB at 1000 → next bill 1001, not 1). 5s timeout;
  // offline skips.
  const primeFromCloudOnce = async () => {
     
    if (readLocal() > 0 || !isOnline()) return;
    const month = counterYYYYMM.value;
    const fromFirestore = getDoc(useFirestore().getReceiptCounterDocRef(month))
      .then((s) => Number(s.data()?.value) || 0)
      .catch(() => 0);
    // TRANSITION: also consult the legacy RTDB counter.
    const fromRtdb = get(child(useRealtimeDatabase().lastReceiptIdRef(), month))
      .then((s) => Number(s.val()) || 0)
      .catch(() => 0);
    await Promise.race([
      Promise.all([fromFirestore, fromRtdb]).then(([a, b]) => raiseTo(Math.max(a, b))),
      new Promise<void>((resolve) => setTimeout(resolve, 5000)),
    ]);
  };

  let booted = false;
  const boot = async () => {
    if (!process.client || booted) return;
    booted = true;
    bootedStoreId = storeId();
    counterYYYYMM.value = usePosJobsStore().getBusinessDate().substring(0, 7).replace("-", "");
    raiseTo(0); // restore last-known local value
    await primeFromCloudOnce();
    loaded.value = true;
    watchCloudCounter();
    watchCrossTabCounter();
  };

  return {
    name: "receiptIdCounter",
    loaded,
    receiptsCounter,
    counterYYYYMM,
    boot,
    getNextReceiptIdAndIncrement,
    flushCounterIncrements,
  };
});
